import React, { useState, useRef, useEffect, useCallback } from "react";
import { TRYON_SERVER_URL } from "../config";
import { useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated } from "../selectors";
import {
  MdOutlineInfo,
  MdOutlineCode,
  MdOutlineTextFields,
  MdOutlineDeleteSweep
} from "react-icons/md";
import { TbBackground } from "react-icons/tb";
import { PiClosedCaptioning, PiCrop, PiMagicWand, PiResize, PiImage } from "react-icons/pi";
import { IoCheckmarkDoneSharp, IoCloseCircle, IoResize } from "react-icons/io5";
import GalleryModal from "../components/GalleryModal";
import { useNavigate } from "react-router-dom";
import AuthenticationRequired from "../components/AuthenticationRequired";
import { MdOutlineFileDownload } from "react-icons/md";
import {
  ReactCompareSlider,
  ReactCompareSliderImage,
} from "react-compare-slider";
import { IoArrowUndoOutline, IoArrowRedoOutline } from "react-icons/io5";
import { MdOutlineCompare } from "react-icons/md";
import { LuImageDown } from "react-icons/lu";
import { IoChatbubblesOutline } from "react-icons/io5";
import { uploadExperimentImage } from "../Utils/ExperimentImageUtils";
import Spinner from "../components/Spinner";
import ChatInterface from "../components/ChatInterface";
import InputContainer from "../components/InputContainer";
import ChangeBackground from "../components/ChangeBackground";
import Resize from "../components/Resize";
import { AiOutlineCloudUpload } from "react-icons/ai";

const Studio = ({ selectedPrompt, setShowPromptGalleryModal }) => {
  const [zoom, setZoom] = useState(100);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [containerHeight, setContainerHeight] = useState("100vh");
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedObj, setSelectedObj] = useState(null);
  const accessToken = useSelector(selectAccessToken);
  const authenticated = useSelector(selectAuthenticated);
  const navigate = useNavigate();
  const [showToastErrorMessage, setShowToastErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorStatus, setErrorStatus] = useState("");
  const [currentImage, setCurrentImage] = useState(null);
  const [imageWithTransparentBackground, setImageWithTransparentBackground] =
    useState(null);
  const [redoStack, setRedoStack] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [showToastSuccessMessage, setShowToastSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [processingMessage, setProcessingMessage] =
    useState("Processing Image");
  const [processingSubMessage, setProcessingSubMessage] = useState(
    "This might take a few moments..."
  );
  const [processingType, setProcessingType] = useState("loading"); // 'loading', 'info', 'warning'
  const [isCropping, setIsCropping] = useState(false);
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [cropStartPos, setCropStartPos] = useState({ x: 0, y: 0 });
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const cropCanvasRef = useRef(null);
  const cropContainerRef = useRef(null);
  const [extraPadding, setExtraPadding] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isCaptioning, setIsCaptioning] = useState(false);
  const [showCaptionOptions, setShowCaptionOptions] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [chatResponses, setChatResponses] = useState([]);
  const chatContainerRef = useRef(null);
  const [isChatProcessing, setIsChatProcessing] = useState(false);
  const [showCaptionRequestResponse, setShowCaptionRequestResponse] =
    useState(false);
  const [captionRequestResponse, setCaptionRequestResponse] = useState("");
  // Add new state for copy toast
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [defaultGenerationSettings, setDefaultGenerationSettings] = useState({
    seed: 42,
    safety_tolerance: 2,
    output_format: "jpeg",
    aspect_ratio: "default",
    prompt_upsampling: false,
    model: "flux-kontext-pro", // 'flux-kontext-pro' or 'flux-kontext-max',
    prompt: "",
  });
  const [addBackgroundMode, setAddBackgroundMode] = useState(false);
  const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(false);
  const [isBackgroundAdded, setIsBackgroundAdded] = useState(false);
  const [resizeImage, setResizeImage] = useState(false);

  const [generationSettings, setGenerationSettings] = useState({
    seed: 42,
    safety_tolerance: 2,
    output_format: "jpeg",
    aspect_ratio: "default",
    prompt_upsampling: false,
    model: "flux-kontext-pro", // 'flux-kontext-pro' or 'flux-kontext-max'
    prompt: "",
  });
  const settingsRef = useRef(null);

  const calculateRemainingHeight = () => {
    const navbar = document.getElementById("navbar");
    if (navbar) {
      const navbarHeight = navbar.offsetHeight;
      const remainingHeight = `calc(100vh - ${navbarHeight}px)`;
      console.log(remainingHeight);
      setContainerHeight(remainingHeight);
    }
  };

  useEffect(() => {
    calculateRemainingHeight();
    // Recalculate on window resize
    window.addEventListener("resize", calculateRemainingHeight);
    return () => window.removeEventListener("resize", calculateRemainingHeight);
  }, [currentImage]);

  const calculateInitialZoom = (
    imgWidth,
    imgHeight,
    containerWidth,
    containerHeight
  ) => {
    // Calculate the scale to fit the image within viewport
    const scaleX = (containerWidth - 40) / imgWidth;
    const scaleY = (containerHeight - 40) / imgHeight;

    // For high-resolution images, we want to ensure they're visible
    // but not too small. Let's set a minimum scale based on the image size
    const minScale = 0.5; // This ensures the image is at least 50% of its original size
    const maxScale = 1.5; // This ensures the image is not larger than 150% of its original size

    // Calculate the scale that would fit the image in the viewport
    let scale = Math.min(scaleX, scaleY);

    // If the image is very large (like 4K), we'll use a different approach
    if (imgWidth > 1920 || imgHeight > 1080) {
      // For 4K images, we'll use a fixed scale that ensures good visibility
      scale = Math.max(minScale, Math.min(maxScale, scale));
    } else {
      // For smaller images, we'll use the calculated scale
      scale = Math.min(scale, 1);
    }

    // Convert scale to zoom percentage
    const calculatedZoom = Math.floor(scale * 100);

    console.log("calculatedZoom", calculatedZoom);

    // Set zoom limits
    const minZoom = 50;
    const maxZoom = 150;

    // Ensure zoom stays within bounds
    const boundedZoom = Math.min(Math.max(calculatedZoom, minZoom), maxZoom);

    console.log("boundedZoom", boundedZoom);

    // If calculated zoom is too low, we'll adjust the container padding instead
    if (calculatedZoom < minZoom) {
      // Calculate how much padding we need to add to maintain minimum zoom
      const paddingScale = minZoom / calculatedZoom;
      const extraPadding = Math.max(
        0,
        (containerWidth * (paddingScale - 1)) / 2
      );

      return {
        zoom: minZoom,
        extraPadding: Math.floor(extraPadding),
      };
    }

    // If calculated zoom is too high, we'll adjust the container padding instead
    if (calculatedZoom > maxZoom) {
      // Calculate how much padding we need to add to maintain maximum zoom
      const paddingScale = maxZoom / calculatedZoom;
      const extraPadding = Math.max(
        0,
        (containerWidth * (paddingScale - 1)) / 2
      );

      return {
        zoom: maxZoom,
        extraPadding: Math.floor(extraPadding),
      };
    }

    return {
      zoom: boundedZoom,
      extraPadding: 0,
    };
  };

  const handleZoomChange = (newZoom) => {
    console.log("handleZoomChange called with:", newZoom);
    // Ensure zoom stays within bounds (50% to 150%)
    const zoomLevel = Math.min(Math.max(newZoom, 50), 150);

    console.log("Setting zoom from handleZoomChange:", zoomLevel);
    // If in cropping mode, exit cropping mode when zoom changes
    if (isCropping) {
      setIsCropping(false);
    }

    // Update zoom state
    setZoom(zoomLevel);

    // Ensure scroll position stays centered after zoom
    if (containerRef.current && imageRef.current) {
      const container = containerRef.current;
      const img = imageRef.current;

      // Get current view center as percentage of total scroll area
      const prevScrollWidth = container.scrollWidth;
      const prevScrollHeight = container.scrollHeight;
      const viewWidth = container.clientWidth;
      const viewHeight = container.clientHeight;

      // Calculate current center position percentages
      const prevCenterXPercent =
        (container.scrollLeft + viewWidth / 2) / prevScrollWidth;
      const prevCenterYPercent =
        (container.scrollTop + viewHeight / 2) / prevScrollHeight;

      // Wait for a tick to let the browser update the DOM with new zoom
      requestAnimationFrame(() => {
        // Apply the same center position percentages to the new scroll area
        const newScrollWidth = container.scrollWidth;
        const newScrollHeight = container.scrollHeight;

        // Calculate new scroll positions
        const newScrollLeft =
          prevCenterXPercent * newScrollWidth - viewWidth / 2;
        const newScrollTop =
          prevCenterYPercent * newScrollHeight - viewHeight / 2;

        // Apply new scroll positions
        container.scrollLeft = newScrollLeft;
        container.scrollTop = newScrollTop;
      });
    }
  };

  // Add effect to handle image load
  useEffect(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      if (img.complete) {
        console.log("Image already loaded, calculating zoom");
        handleImageLoad();
      } else {
        console.log("Setting up image load handler");
        img.onload = handleImageLoad;
      }
    }
  }, [currentImage]);

  const handleImageLoad = () => {
    console.log("Image load handler called");
    if (containerRef.current && imageRef.current) {
      const container = containerRef.current;
      const img = imageRef.current;

      console.log(
        "Image loaded - natural dimensions:",
        img.naturalWidth,
        img.naturalHeight
      );
      const { zoom: newZoom, extraPadding: newPadding } = calculateInitialZoom(
        img.naturalWidth,
        img.naturalHeight,
        container.clientWidth,
        container.clientHeight
      );

      console.log("Setting initial zoom from image load:", newZoom);
      setZoom(newZoom);
      setExtraPadding(newPadding);
      setIsImageLoaded(true);

      // Center the image
      setTimeout(() => {
        container.scrollLeft =
          (container.scrollWidth - container.clientWidth) / 2;
        container.scrollTop =
          (container.scrollHeight - container.clientHeight) / 2;
      }, 50);
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 100 && !isCropping) {
      // Prevent default behavior to avoid text selection during drag
      e.preventDefault();

      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
      });

      // Change cursor style to indicate grabbing
      if (containerRef.current) {
        containerRef.current.style.cursor = "grabbing";
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 100 && !isCropping) {
      e.preventDefault();

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      if (containerRef.current) {
        const container = containerRef.current;

        // Use a multiplier to make dragging feel more responsive at higher zoom levels
        const dragSpeed = zoom > 150 ? 1.5 : 1;

        // Adjust the scroll position based on mouse movement
        container.scrollLeft -= deltaX * dragSpeed;
        container.scrollTop -= deltaY * dragSpeed;

        // Update drag start position for next move event
        setDragStart({
          x: e.clientX,
          y: e.clientY,
        });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);

    // Reset cursor style
    if (containerRef.current) {
      containerRef.current.style.cursor = zoom > 100 ? "grab" : "default";
    }
  };

  // Add an effect to update cursor style when zoom changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.cursor =
        zoom > 100 && !isCropping ? "grab" : "default";
    }
  }, [zoom, isCropping]);

  // Add a function to save image state to history
  const saveImageToHistory = (imageUrl) => {
    // Add current image to undo stack if it exists
    if (currentImage) {
      setUndoStack((prev) => [...prev, currentImage]);
    }
    // Clear redo stack when new image is added
    setRedoStack([]);
    // Update current image
    setCurrentImage(imageUrl);

    // Disable compare mode when adding a new image to history
    if (isCompareMode) {
      setIsCompareMode(false);
    }

    if (isCaptioning) {
      setIsCaptioning(false);
      setShowCaptionOptions(false);
    }
  };

  // Handle undo operation
  const handleUndo = () => {
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    if (undoStack.length > 0) {
      // Get the last image from undo stack
      const previousImage = undoStack[undoStack.length - 1];

      // Add current image to redo stack if it exists
      if (currentImage) {
        setRedoStack((prev) => [...prev, currentImage]);
      }

      // Update undo stack and current image
      setUndoStack((prev) => prev.slice(0, -1));
      setCurrentImage(previousImage);

      // Reset transparent background image
      setImageWithTransparentBackground(null);

      // Disable compare mode when undoing
      if (isCompareMode) {
        setIsCompareMode(false);
      }

      if (isCaptioning) {
        setIsCaptioning(false);
        setShowCaptionOptions(false);
      }
    }
  };

  // Handle redo operation
  const handleRedo = () => {
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    if (redoStack.length > 0) {
      // Get the last image from redo stack
      const nextImage = redoStack[redoStack.length - 1];

      // Add current image to undo stack if it exists
      if (currentImage) {
        setUndoStack((prev) => [...prev, currentImage]);
      }

      // Update redo stack and current image
      setRedoStack((prev) => prev.slice(0, -1));
      setCurrentImage(nextImage);

      // Reset transparent background image
      setImageWithTransparentBackground(null);

      // Disable compare mode when redoing
      if (isCompareMode) {
        setIsCompareMode(false);
      }

      if (isCaptioning) {
        setIsCaptioning(false);
        setShowCaptionOptions(false);
      }
    }
  };

  const handleFileSelected = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log("File selected, resetting image loaded state");
      setIsImageLoaded(false);
      const imageUrl = URL.createObjectURL(file);
      console.log("Selected Image URL:", imageUrl);
      setCurrentImage(imageUrl);
      saveImageToHistory(imageUrl);
      setImageWithTransparentBackground(null);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      console.log("File dropped, resetting image loaded state");
      setIsImageLoaded(false);
      const imageUrl = URL.createObjectURL(file);
      console.log("Dropped Image URL:", imageUrl);
      setCurrentImage(imageUrl);
      saveImageToHistory(imageUrl);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleImageUpload = async (
    file,
    action,
    backgroundImage = null,
    backgroundColor = null,
    gradientPattern = true
  ) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("action", action);

    try {
      const responseImage = await fetch(`${TRYON_SERVER_URL}/api/v1/studio/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (responseImage.status !== 200) {
        setShowToastErrorMessage(true);
        setErrorMessage(
          "Error processing image. Please try again. If the problem persists, please contact support at support@tryon.ai!"
        );
        setErrorStatus("Error");
        setIsProcessing(false);
        return;
      }

      const imageBlob = await responseImage.blob();
      const imageUrl = URL.createObjectURL(imageBlob);

      if (action === "remove_background") {
        setIsBackgroundRemoved(true);
        setImageWithTransparentBackground(imageUrl);
        await addBackground(
          imageUrl,
          backgroundColor,
          gradientPattern,
          backgroundImage
        );
      } else {
        setCurrentImage(imageUrl);
        setIsProcessing(false);
        // Save processed image to history
        saveImageToHistory(imageUrl);
        setImageWithTransparentBackground(null);
      }
    } catch (error) {
      console.error("Error in handleImageUpload:", error);
      setShowToastErrorMessage(true);
      setErrorMessage("Error processing image. Please try again.");
      setErrorStatus("Error");
      setIsProcessing(false);
    }
  };

  /**
   * Add a white background to the image
   * @param {string} imageUrl - The URL of the image to add a background to
   */
  const addBackground = async (
    imageUrl,
    backgroundColor = null,
    gradientPattern = true,
    backgroundImage = null
  ) => {
    // setImageWithTransparentBackground(imageUrl);
    console.log(
      "addBackground:",
      imageUrl,
      backgroundColor,
      gradientPattern,
      backgroundImage
    );

    if (backgroundImage) {
      setIsProcessing(true);
      setProcessingMessage("Adding Background");
      setProcessingSubMessage("Adding background to your image...");
      setProcessingType("loading");
    }

    // load image and draw it on the canvas
    const img = new Image();
    img.crossOrigin = "anonymous"; // Add CORS support
    img.onload = () => {
      // Create a canvas with the same dimensions as the image
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      if (backgroundColor) {
        // Fill the canvas with white
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setIsBackgroundAdded(true);
      }

      if (gradientPattern) {
        // Create a pattern canvas
        const patternCanvas = document.createElement("canvas");
        patternCanvas.width = 20;
        patternCanvas.height = 20;
        const patternCtx = patternCanvas.getContext("2d");

        // Draw the gradient pattern
        patternCtx.fillStyle = "#ccc";
        patternCtx.fillRect(0, 0, 10, 10);
        patternCtx.fillRect(10, 10, 10, 10);

        // Create pattern and fill the main canvas
        const pattern = ctx.createPattern(patternCanvas, "repeat");
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // draw the background image on the canvas
      if (backgroundImage) {
        console.log("backgroundImage:", backgroundImage);
        // Use the image URL directly since it's already a blob URL
        const bgImage = new Image();
        bgImage.crossOrigin = "anonymous";
        bgImage.src = backgroundImage;
        bgImage.onload = () => {
          // Calculate dimensions to cover the entire canvas while maintaining aspect ratio
          const bgImageWidth = bgImage.width;
          const bgImageHeight = bgImage.height;
          const canvasRatio = canvas.width / canvas.height;
          const imageRatio = bgImageWidth / bgImageHeight;

          let drawWidth,
            drawHeight,
            offsetX = 0,
            offsetY = 0;

          if (imageRatio > canvasRatio) {
            // Image is wider than canvas ratio
            drawHeight = canvas.height;
            drawWidth = drawHeight * imageRatio;
            offsetX = (canvas.width - drawWidth) / 2;
          } else {
            // Image is taller than canvas ratio
            drawWidth = canvas.width;
            drawHeight = drawWidth / imageRatio;
            offsetY = (canvas.height - drawHeight) / 2;
          }

          // Draw the background image to cover the entire canvas
          ctx.drawImage(bgImage, offsetX, offsetY, drawWidth, drawHeight);

          // draw the main image on the canvas
          ctx.drawImage(img, 0, 0);

          // convert the canvas to a data URL
          const finalImageUrl = canvas.toDataURL("image/png");
          setCurrentImage(finalImageUrl);
          setIsProcessing(false);
          // Save to history
          saveImageToHistory(finalImageUrl);
          setIsBackgroundAdded(true);
        };
      } else {
        // draw the main image on the canvas
        ctx.drawImage(img, 0, 0);

        const finalImageUrl = canvas.toDataURL("image/png");
        setCurrentImage(finalImageUrl);
        setIsProcessing(false);
        // Save to history
        saveImageToHistory(finalImageUrl);
      }
    };
    img.src = imageUrl;
  };

  const handleTool = async (
    action,
    backgroundImage = null,
    backgroundColor = null,
    gradientPattern = true
  ) => {
    setIsProcessing(true);

    setIsBackgroundRemoved(false);

    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    if (isCaptioning) {
      setIsCaptioning(false);
      setShowCaptionOptions(false);
    }

    if (isCompareMode) {
      setIsCompareMode(false);
    }

    if (showChat) {
      setShowChat(false);
    }

    if (
      action === "remove_background" &&
      imageWithTransparentBackground !== null
    ) {
      addBackground(
        imageWithTransparentBackground,
        backgroundColor,
        gradientPattern,
        backgroundImage
      );
      return;
    }

    // Reset transparent background image for any tool action
    setImageWithTransparentBackground(null);

    // Set appropriate processing messages based on action
    if (action === "remove_background") {
      setProcessingMessage("Removing Background");
      setProcessingSubMessage("Separating your subject from the background...");
    } else if (action === "upscale_image") {
      setProcessingMessage("Enhancing Image Quality");
      setProcessingSubMessage("Applying AI upscaling to improve resolution...");
    }

    setProcessingType("loading");

    try {
      // Fetch the image data from the URL
      const response = await fetch(currentImage);
      const blob = await response.blob();

      // Create a File object from the blob
      const file = new File([blob], "image.png", { type: "image/png" });

      // check the width and height of the image
      if (action === "upscale_image") {
        const image = new Image();
        image.onload = () => {
          console.log("Image dimensions:", image.width, image.height);
          if (image.width > 1256 || image.height > 1256) {
            console.log("image is too large");
            setShowToastErrorMessage(true);
            setErrorMessage(
              `Image size (${image.width}×${image.height}px) exceeds the maximum dimensions. Upscaling is limited to images smaller than 1256×1256px.`
            );
            setErrorStatus("Size Limit Exceeded");
            setIsProcessing(false);
            return;
          }
          // Continue with the upload if image size is valid
          handleImageUpload(
            file,
            action,
            backgroundImage,
            backgroundColor,
            gradientPattern
          );
        };
        image.src = URL.createObjectURL(file);
        return; // Return here to prevent the code below from executing immediately
      }

      // Handle other actions or continue with upload for non-upscale actions
      handleImageUpload(
        file,
        action,
        backgroundImage,
        backgroundColor,
        gradientPattern
      );
    } catch (error) {
      console.error("Error processing image:", error);
      setShowToastErrorMessage(true);
      setErrorMessage(
        "Unable to process your image. Please try again with a different image."
      );
      setErrorStatus("Processing Error");
      setIsProcessing(false);
    }
  };

  const handleErrorClose = () => {
    setShowToastErrorMessage(false);
    setErrorMessage("");
    setErrorStatus("");
  };

  const handleSuccessClose = () => {
    setShowToastSuccessMessage(false);
    setSuccessMessage("");
  };

  // Function to show status message
  const showStatusMessage = (
    message,
    subMessage,
    type = "info",
    duration = 3000
  ) => {
    setProcessingMessage(message);
    setProcessingSubMessage(subMessage);
    setProcessingType(type);
    setIsProcessing(true);

    // Auto-hide after duration if provided
    if (duration) {
      setTimeout(() => {
        setIsProcessing(false);
      }, duration);
    }
  };

  // Example usage in handleDownloadImage
  const handleDownloadImage = () => {
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    if (!currentImage && !imageWithTransparentBackground) {
      setShowToastErrorMessage(true);
      setErrorMessage("Please select or create an image first");
      setErrorStatus("No Image Selected");
      return;
    }

    showStatusMessage(
      "Preparing Download",
      "Your image will be saved to your device shortly...",
      "info",
      1500
    );

    if (imageWithTransparentBackground && !isBackgroundAdded) {
      const link = document.createElement("a");
      link.href = imageWithTransparentBackground;
      link.download = "image.png";
      link.click();
    } else {
      const link = document.createElement("a");
      link.href = currentImage;
      link.download = "image.png";
      link.click();
    }
  };

  // Add a function to handle image selection from gallery
  const handleGalleryImageSelect = (imageUrl) => {
    if (isCropping) {
      setIsCropping(false);
    }

    console.log("Gallery image selected, resetting image loaded state");
    setIsImageLoaded(false);
    setCurrentImage(imageUrl);
    setImageWithTransparentBackground(null);
    saveImageToHistory(imageUrl);
  };

  // Add a function to handle comparison toggle
  const handleCompareToggle = () => {
    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }

    // Only enable compare mode if we have both a current image and at least one image in the undo stack
    if (currentImage && undoStack.length > 0) {
      // Log image dimensions before comparison
      const prevImage = new Image();
      const currImage = new Image();

      prevImage.onload = () => {
        console.log(
          "Previous image dimensions:",
          prevImage.naturalWidth,
          "x",
          prevImage.naturalHeight
        );
      };

      currImage.onload = () => {
        console.log(
          "Current image dimensions:",
          currImage.naturalWidth,
          "x",
          currImage.naturalHeight
        );
      };

      prevImage.src = undoStack[undoStack.length - 1];
      currImage.src = currentImage;

      setIsCompareMode(!isCompareMode);
    } else if (!currentImage) {
      setShowToastErrorMessage(true);
      setErrorMessage("No image available to compare");
      setErrorStatus("Error");
    } else if (undoStack.length === 0) {
      setShowToastErrorMessage(true);
      setErrorMessage("No previous image available to compare");
      setErrorStatus("Error");
    }
  };

  const handleSaveToGallery = async () => {
    console.log("Save to gallery");

    // Exit crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    try {
      if (!currentImage) {
        setShowToastErrorMessage(true);
        setErrorMessage("No image available to save to your gallery");
        setErrorStatus("Missing Image");
        return;
      }

      // Show processing status
      setIsProcessing(true);
      setProcessingMessage("Saving to Your Gallery");
      setProcessingSubMessage("Uploading and processing your image...");
      setProcessingType("loading");

      // Convert currentImage URL to file
      const response = await fetch(currentImage);
      const blob = await response.blob();

      // Create a File object with appropriate name and type
      const fileName = `image_${Date.now()}.png`;
      const file = new File([blob], fileName, { type: "image/png" });

      const result = await uploadExperimentImage(file, accessToken, {});
      console.log("result:", result);

      // Hide processing status
      setIsProcessing(false);

      if (result.status === "success") {
        console.log("Image saved to gallery");
        setShowToastSuccessMessage(true);
        setSuccessMessage("Image successfully added to your gallery");
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowToastSuccessMessage(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        setShowToastErrorMessage(true);
        setErrorMessage(
          "Unable to save image to your gallery. Please try again."
        );
        setErrorStatus("Save Failed");
      }
    } catch (error) {
      console.error("Error saving to gallery:", error);
      setIsProcessing(false);
      setShowToastErrorMessage(true);
      setErrorMessage(
        "Connection issue while saving your image. Please check your internet connection and try again."
      );
      setErrorStatus("Connection Error");
    }
  };

  const handleCrop = () => {
    // Disable chat mode when entering crop mode
    setShowChat(false);
    setIsCaptioning(false);
    setShowCaptionOptions(false);

    // Disable compare mode if active
    if (isCompareMode) {
      setIsCompareMode(false);
    }

    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }

    // Reset transparent background image when entering crop mode
    setImageWithTransparentBackground(null);

    if (isCropping) {
      // If already in cropping mode, exit without applying crop
      setIsCropping(false);
    } else {
      // Enter cropping mode
      setIsCropping(true);

      // If zoomed in, reset to 100% for easier cropping
      if (zoom !== 100) {
        // The useEffect for zoom will trigger initializeCropBox
        setZoom(100);
      } else {
        // If already at 100%, initialize the crop box directly
        initializeCropBox();
      }
    }
  };

  // Move initializeCropBox to be a useCallback
  const initializeCropBox = useCallback(() => {
    const canvas = cropCanvasRef.current;
    const img = imageRef.current;
    if (canvas && img) {
      // Get the actual dimensions of the image as rendered
      const imgRect = img.getBoundingClientRect();

      // Set canvas dimensions to match the image
      canvas.width = imgRect.width;
      canvas.height = imgRect.height;

      // Set initial crop box to center of image with 80% dimensions
      const width = imgRect.width * 0.8;
      const height = imgRect.height * 0.8;
      const x = (imgRect.width - width) / 2;
      const y = (imgRect.height - height) / 2;

      setCropBox({ x, y, width, height });
    }
  }, [cropCanvasRef, imageRef]);

  const applyCrop = () => {
    if (!isCropping || !currentImage || !cropBox.width || !cropBox.height) {
      setIsCropping(false);
      return;
    }

    // Create a canvas to draw the cropped image
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Get the image element and its dimensions
      const imageElem = imageRef.current;
      if (!imageElem) {
        console.error("Image element not found");
        setIsCropping(false);
        return;
      }

      // Get the actual displayed image dimensions
      const imgRect = imageElem.getBoundingClientRect();

      // Calculate scaling ratio between displayed and natural image
      const scaleX = img.naturalWidth / imgRect.width;
      const scaleY = img.naturalHeight / imgRect.height;

      // Apply scaling to get actual crop coordinates on the natural image
      const sourceX = cropBox.x * scaleX;
      const sourceY = cropBox.y * scaleY;
      const sourceWidth = cropBox.width * scaleX;
      const sourceHeight = cropBox.height * scaleY;

      // Set canvas dimensions to the cropped size
      canvas.width = sourceWidth;
      canvas.height = sourceHeight;

      // Draw the cropped portion to the canvas
      try {
        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          sourceWidth,
          sourceHeight
        );

        // Convert canvas to new image URL
        const croppedImageUrl = canvas.toDataURL("image/png");

        // Show success message
        showStatusMessage(
          "Image Cropped",
          "Your image has been successfully cropped",
          "info",
          1500
        );

        // Save to history and update current image
        saveImageToHistory(croppedImageUrl);
        setCurrentImage(croppedImageUrl);

        // Exit cropping mode
        setIsCropping(false);
      } catch (error) {
        console.error("Error cropping image:", error);
        showStatusMessage(
          "Crop Failed",
          "Unable to crop image. Please try again.",
          "warning",
          2000
        );
        setIsCropping(false);
      }
    };

    img.onerror = () => {
      console.error("Failed to load image for cropping");
      showStatusMessage(
        "Crop Failed",
        "Unable to load image for cropping",
        "warning",
        2000
      );
      setIsCropping(false);
    };

    img.src = currentImage;
  };

  const cancelCrop = () => {
    setIsCropping(false);
  };

  const handleCropMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isCropping || isProcessing) return;

    const canvas = cropCanvasRef.current;
    if (!canvas) return;

    // Get canvas coordinates
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    // Check if clicking on a resize handle
    const handleSize = 12; // Increased handle hit area
    const isInside = (px, py, hx, hy) =>
      px >= hx - handleSize &&
      px <= hx + handleSize &&
      py >= hy - handleSize &&
      py <= hy + handleSize;

    // Check each resize handle
    if (isInside(x, y, cropBox.x, cropBox.y)) {
      setResizeHandle("nw");
    } else if (isInside(x, y, cropBox.x + cropBox.width, cropBox.y)) {
      setResizeHandle("ne");
    } else if (isInside(x, y, cropBox.x, cropBox.y + cropBox.height)) {
      setResizeHandle("sw");
    } else if (
      isInside(x, y, cropBox.x + cropBox.width, cropBox.y + cropBox.height)
    ) {
      setResizeHandle("se");
    } else if (isInside(x, y, cropBox.x + cropBox.width / 2, cropBox.y)) {
      setResizeHandle("n");
    } else if (
      isInside(x, y, cropBox.x + cropBox.width, cropBox.y + cropBox.height / 2)
    ) {
      setResizeHandle("e");
    } else if (
      isInside(x, y, cropBox.x + cropBox.width / 2, cropBox.y + cropBox.height)
    ) {
      setResizeHandle("s");
    } else if (isInside(x, y, cropBox.x, cropBox.y + cropBox.height / 2)) {
      setResizeHandle("w");
    } else if (
      x > cropBox.x &&
      x < cropBox.x + cropBox.width &&
      y > cropBox.y &&
      y < cropBox.y + cropBox.height
    ) {
      // Clicking inside crop box - drag the whole box
      setIsDraggingCrop(true);
    } else {
      // Clicking outside - start a new crop box
      const minSize = 100; // Minimum size for new crop box
      setCropBox({
        x: Math.max(0, x - minSize / 2),
        y: Math.max(0, y - minSize / 2),
        width: minSize,
        height: minSize,
      });
      setResizeHandle("se");
    }

    setCropStartPos({ x, y });
  };

  const handleCropMouseMove = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isCropping || (!isDraggingCrop && !resizeHandle)) return;

    const canvas = cropCanvasRef.current;
    if (!canvas) return;

    // Get canvas coordinates
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const dx = x - cropStartPos.x;
    const dy = y - cropStartPos.y;

    if (isDraggingCrop) {
      // Move the entire crop box
      setCropBox((prev) => ({
        ...prev,
        x: Math.max(0, Math.min(canvas.width - prev.width, prev.x + dx)),
        y: Math.max(0, Math.min(canvas.height - prev.height, prev.y + dy)),
      }));
      setCropStartPos({ x, y });
    } else if (resizeHandle) {
      let newBox = { ...cropBox };

      // Handle resizing based on which handle is being dragged
      switch (resizeHandle) {
        case "nw":
          newBox.width = Math.max(10, newBox.width - dx);
          newBox.height = Math.max(10, newBox.height - dy);
          newBox.x = Math.min(newBox.x + dx, newBox.x + newBox.width - 10);
          newBox.y = Math.min(newBox.y + dy, newBox.y + newBox.height - 10);
          break;
        case "ne":
          newBox.width = Math.max(10, newBox.width + dx);
          newBox.height = Math.max(10, newBox.height - dy);
          newBox.y = Math.min(newBox.y + dy, newBox.y + newBox.height - 10);
          break;
        case "sw":
          newBox.width = Math.max(10, newBox.width - dx);
          newBox.height = Math.max(10, newBox.height + dy);
          newBox.x = Math.min(newBox.x + dx, newBox.x + newBox.width - 10);
          break;
        case "se":
          newBox.width = Math.max(10, newBox.width + dx);
          newBox.height = Math.max(10, newBox.height + dy);
          break;
        case "n":
          newBox.height = Math.max(10, newBox.height - dy);
          newBox.y = Math.min(newBox.y + dy, newBox.y + newBox.height - 10);
          break;
        case "e":
          newBox.width = Math.max(10, newBox.width + dx);
          break;
        case "s":
          newBox.height = Math.max(10, newBox.height + dy);
          break;
        case "w":
          newBox.width = Math.max(10, newBox.width - dx);
          newBox.x = Math.min(newBox.x + dx, newBox.x + newBox.width - 10);
          break;
        default:
          break;
      }

      // Ensure crop box stays within image bounds
      newBox.x = Math.max(0, Math.min(canvas.width - 10, newBox.x));
      newBox.y = Math.max(0, Math.min(canvas.height - 10, newBox.y));
      newBox.width = Math.max(
        10,
        Math.min(canvas.width - newBox.x, newBox.width)
      );
      newBox.height = Math.max(
        10,
        Math.min(canvas.height - newBox.y, newBox.height)
      );

      setCropBox(newBox);
      setCropStartPos({ x, y });
    }
  };

  const handleCropMouseUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCrop(false);
    setResizeHandle(null);
  };

  // Draw crop overlay on canvas
  useEffect(() => {
    if (!isCropping || !cropCanvasRef.current || !imageRef.current) return;

    const canvas = cropCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;

    if (!ctx) return;

    // Get the actual dimensions of the image as rendered
    const imgRect = img.getBoundingClientRect();

    // Make sure canvas dimensions match the image
    if (canvas.width !== imgRect.width || canvas.height !== imgRect.height) {
      canvas.width = imgRect.width;
      canvas.height = imgRect.height;
    }

    // Clear canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw semi-transparent overlay
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Clear the crop area
    ctx.clearRect(cropBox.x, cropBox.y, cropBox.width, cropBox.height);

    // Draw crop border with shadow
    ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.strokeRect(cropBox.x, cropBox.y, cropBox.width, cropBox.height);

    // Reset shadow for handles
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;

    // Draw resize handles
    const handleSize = 8;
    const drawHandle = (x, y) => {
      // Draw handle shadow
      ctx.beginPath();
      ctx.arc(x, y, handleSize + 1, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
      ctx.fill();

      // Draw handle
      ctx.beginPath();
      ctx.arc(x, y, handleSize, 0, Math.PI * 2);
      ctx.fillStyle = "white";
      ctx.fill();

      // Draw handle border
      ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw inner highlight
      ctx.beginPath();
      ctx.arc(x, y, handleSize - 1, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    // Corner handles
    drawHandle(cropBox.x, cropBox.y); // Top-left
    drawHandle(cropBox.x + cropBox.width, cropBox.y); // Top-right
    drawHandle(cropBox.x, cropBox.y + cropBox.height); // Bottom-left
    drawHandle(cropBox.x + cropBox.width, cropBox.y + cropBox.height); // Bottom-right

    // Edge handles
    drawHandle(cropBox.x + cropBox.width / 2, cropBox.y); // Top-center
    drawHandle(cropBox.x + cropBox.width, cropBox.y + cropBox.height / 2); // Right-center
    drawHandle(cropBox.x + cropBox.width / 2, cropBox.y + cropBox.height); // Bottom-center
    drawHandle(cropBox.x, cropBox.y + cropBox.height / 2); // Left-center
  }, [isCropping, cropBox]);

  // Add an effect to update the crop canvas when the zoom changes
  useEffect(() => {
    if (isCropping) {
      // Small delay to allow the zoom render to complete
      setTimeout(() => {
        initializeCropBox();
      }, 100);
    }
  }, [zoom, isCropping, initializeCropBox]);

  const handleCaption = () => {
    if (isProcessing || currentImage === null) return;

    // Disable chat mode if active
    if (showChat) {
      setShowChat(false);
    }

    // Disable crop mode if active
    if (isCropping) {
      setIsCropping(false);
    }

    // Disable compare mode if active
    if (isCompareMode) {
      setIsCompareMode(false);
    }

    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }

    if(resizeImage) {
      setResizeImage(false);
    }

    setIsCaptioning(!isCaptioning);

    // Toggle caption options
    setShowCaptionOptions(!showCaptionOptions);
  };

  const handleCaptionOption = async (option) => {
    setShowCaptionOptions(false);

    setIsProcessing(true);
    setProcessingType("loading");
    setProcessingMessage("Generating caption...");
    setProcessingSubMessage("Please wait...");

    try {
      // Fetch the image data from the URL
      const imageResponse = await fetch(currentImage);
      const imageBlob = await imageResponse.blob();

      // Create a File object from the blob
      const file = new File([imageBlob], "image.png", { type: "image/png" });

      const formdata = new FormData();
      formdata.append("image", file);
      formdata.append("format", option);

      const response = await fetch(`${TRYON_SERVER_URL}/api/v1/studio/chat/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formdata,
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Caption:", data);
        setIsProcessing(false);
        setShowCaptionRequestResponse(true);

        // Format the response based on the option
        if (option === "json") {
          try {
            // If it's already an object, stringify it with proper formatting
            const formattedJson =
              typeof data.caption === "object"
                ? JSON.stringify(data.caption, null, 2)
                : JSON.stringify(JSON.parse(data.caption), null, 2);
            setCaptionRequestResponse(formattedJson);
          } catch (e) {
            // If parsing fails, show the raw response
            setCaptionRequestResponse(data.caption);
          }
        } else {
          setCaptionRequestResponse(data.caption);
        }
      } else {
        console.error("Error:", response.statusText);
        setIsProcessing(false);
        setShowCaptionRequestResponse(true);
        setCaptionRequestResponse("Unable to generate caption for this image!");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsProcessing(false);
      setShowCaptionRequestResponse(true);
      setCaptionRequestResponse(
        "An error occurred while generating the caption."
      );
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isChatProcessing) return;

    setIsChatProcessing(true);
    const currentQuery = chatInput;
    setChatInput("");

    // Add the user's message and a processing state
    setChatResponses((prev) => [
      ...prev,
      {
        query: currentQuery,
        response: "processing",
      },
    ]);

    try {
      const imageResponse = await fetch(currentImage);
      const imageBlob = await imageResponse.blob();
      const file = new File([imageBlob], "image.png", { type: "image/png" });

      const formdata = new FormData();
      formdata.append("image", file);
      formdata.append("prompt", currentQuery);

      const response = await fetch(`${TRYON_SERVER_URL}/api/v1/studio/chat/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formdata,
      });

      if (response.ok) {
        const data = await response.json();
        // Update the last response with the actual response
        setChatResponses((prev) => {
          const newResponses = [...prev];
          newResponses[newResponses.length - 1] = {
            query: currentQuery,
            response: data.caption || "No response received",
          };
          return newResponses;
        });
      } else {
        // Update the last response with an error message
        setChatResponses((prev) => {
          const newResponses = [...prev];
          newResponses[newResponses.length - 1] = {
            query: currentQuery,
            response:
              "Sorry, I encountered an error while processing your request. Please try again.",
          };
          return newResponses;
        });
      }
    } catch (error) {
      console.error("Error:", error);
      // Update the last response with an error message
      setChatResponses((prev) => {
        const newResponses = [...prev];
        newResponses[newResponses.length - 1] = {
          query: currentQuery,
          response:
            "Sorry, I encountered an error while processing your request. Please try again.",
        };
        return newResponses;
      });
    } finally {
      setIsChatProcessing(false);
    }
  };

  const handleGenerateEditImage = async () => {
    if (!generationSettings.prompt.trim() || isProcessing) return;

    console.log("settings", generationSettings);

    setIsProcessing(true);
    setProcessingType("loading");
    setProcessingMessage("Processing message...");
    setProcessingSubMessage("Please wait...");

    try {
      const formdata = new FormData();

      if (currentImage) {
        // Convert currentImage URL to file
        const imageResponse = await fetch(currentImage);
        const blob = await imageResponse.blob();
        const file = new File([blob], "image.png", { type: "image/png" });
        formdata.append("image", file);
      }

      formdata.append("prompt", generationSettings.prompt);
      formdata.append("seed", generationSettings.seed);
      formdata.append("safety_tolerance", generationSettings.safety_tolerance);
      formdata.append("output_format", generationSettings.output_format);
      formdata.append("aspect_ratio", generationSettings.aspect_ratio);
      formdata.append(
        "prompt_upsampling",
        generationSettings.prompt_upsampling
      );
      formdata.append("model", generationSettings.model);

      console.log("formdata", formdata);

      const response = await fetch(
        `${TRYON_SERVER_URL}/api/v1/flux/generate/`,
        {
          method: "POST",
          body: formdata,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("response", response);

      if (response.ok) {
        const data = await response.json();
        console.log("data", data);

        const imageUrl = data.result.image_url;
        console.log("imageUrl", `${TRYON_SERVER_URL}/${imageUrl}`);
        setCurrentImage(`${TRYON_SERVER_URL}/${imageUrl}`);
        saveImageToHistory(`${TRYON_SERVER_URL}/${imageUrl}`);
        setGenerationSettings(defaultGenerationSettings);

        setIsProcessing(false);
        setShowToastSuccessMessage(true);
        setSuccessMessage("Image processed successfully");
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowToastSuccessMessage(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        // Update the last response with an error message
        setChatResponses((prev) => {
          const newResponses = [...prev];
          newResponses[newResponses.length - 1] = {
            query: generationSettings.prompt,
            response:
              "Sorry, I encountered an error while processing your message. Please try again.",
          };
          return newResponses;
        });
        setIsProcessing(false);
        setShowToastErrorMessage(true);
        setErrorMessage("Error processing message. Please try again.");
        setErrorStatus("Processing Error");
      }
    } catch (error) {
      console.error("Error:", error);
      // Update the last response with an error message
      setChatResponses((prev) => {
        const newResponses = [...prev];
        newResponses[newResponses.length - 1] = {
          query: generationSettings.prompt,
          response:
            "Sorry, I encountered an error while processing your message. Please try again.",
        };
        return newResponses;
      });
      setIsProcessing(false);
      setShowToastErrorMessage(true);
      setErrorMessage("Error processing message. Please try again.");
      setErrorStatus("Processing Error");
    }
  };

  // Processing Status Component
  const ProcessingStatus = () => {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-30 bg-black/30 backdrop-blur-sm">
        <div className="flex flex-col items-center justify-center max-w-sm bg-white/95 border border-rose-100 rounded-lg mx-auto w-full px-8 py-6 shadow-xl animate-fade-in-down">
          <div className="text-center space-y-4">
            {processingType === "loading" && (
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin"></div>
              </div>
            )}
            {processingType === "info" && (
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-500 rounded-full">
                  <MdOutlineInfo className="w-8 h-8" />
                </div>
              </div>
            )}
            {processingType === "warning" && (
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-500 rounded-full">
                  <svg
                    className="w-8 h-8"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </div>
              </div>
            )}
            <div className="mt-4">
              <p className="text-gray-700 font-semibold text-lg">
                {processingMessage}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {processingSubMessage}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Update the chat button click handler
  const handleChatToggle = () => {
    if (isProcessing || currentImage === null) return;

    if (addBackgroundMode) {
      setAddBackgroundMode(false);
    }
    if (resizeImage) {
      setResizeImage(false);
    }

    // If chat is being enabled, disable crop mode
    if (!showChat) {
      if (isCropping) {
        setIsCropping(false);
      }
      if (isCaptioning) {
        setIsCaptioning(false);
        setShowCaptionOptions(false);
      }
    }

    setShowChat(!showChat);
  };

  // Add effect to handle auto-scrolling in chat
  useEffect(() => {
    if (chatContainerRef.current && chatResponses.length > 0) {
      const container = chatContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [chatResponses]);

  // Update the caption response display
  const CaptionResponse = () => {
    if (!showCaptionRequestResponse) return null;

    const isJson =
      captionRequestResponse.trim().startsWith("{") ||
      captionRequestResponse.trim().startsWith("[");

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(captionRequestResponse);
        setShowCopyToast(true);
        // Auto hide toast after 2 seconds
        setTimeout(() => setShowCopyToast(false), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    };

    return (
      <>
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm">
          <div className="max-w-2xl w-full mx-4 bg-white rounded-lg shadow-xl border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Generated Caption
              </h3>
              <button
                onClick={() => setShowCaptionRequestResponse(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4 h-[60vh] overflow-y-auto">
              {isJson ? (
                <pre className="bg-gray-50 rounded-lg p-4 overflow-x-auto text-sm">
                  <code className="text-gray-800">
                    {captionRequestResponse}
                  </code>
                </pre>
              ) : (
                <div className="text-gray-700 whitespace-pre-wrap">
                  {captionRequestResponse}
                </div>
              )}
            </div>
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={handleCopy}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-b from-secondary to-secondary-800 hover:bg-gradient-to-b hover:from-secondary-800 hover:to-secondary rounded-lg transition-colors"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>

        {/* Copy Toast */}
        {showCopyToast && (
          <div className="fixed bottom-4 left-4 z-50 animate-fade-in-up">
            <div className="flex items-center p-4 mb-4 text-gray-500 bg-white rounded-lg shadow-lg border border-gray-200">
              <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded-lg">
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                </svg>
              </div>
              <div className="ms-3 text-sm font-normal">
                Text copied to clipboard
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Add click outside handler for settings dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="flex flex-col bg-gradient-to-b from-rose-50 to-amber-50"
      style={{ height: containerHeight }}
    >
      <div
        className="flex overflow-auto relative"
        style={{ height: containerHeight }}
      >
        {authenticated === null ? (
          <div className="flex w-full h-full">
            {/* Main content area skeleton */}
            <div className="flex-1 m-1">
              <div className="w-full h-full flex items-center justify-center bg-white/80 shadow-md rounded-lg animate-pulse">
                <Spinner />
              </div>
            </div>

            {/* Tools sidebar skeleton */}
            <div className="w-14 md:w-16 lg:w-18 ml-1 p-1">
              <div className="h-full bg-white/80 rounded-lg shadow-md animate-pulse">
                <div className="h-10 bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-lg"></div>
                <div className="p-2 space-y-2">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center p-3 border-b border-slate-100"
                    >
                      <div className="w-8 h-8 bg-slate-100 rounded-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : authenticated ? (
          <>
            <div className="size-20 grow" style={{ height: containerHeight }}>
              {!currentImage ? (
                <div
                  className="flex flex-col items-center justify-start pt-12"
                  style={{ height: containerHeight }}
                >
                  <div className="flex flex-col items-center justify-center text-center mb-12">
                    <div className="relative">
                      <div className="text-5xl font-bold bg-gradient-to-r from-secondary to-secondary-800 bg-clip-text text-transparent mb-2">
                        Studio
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-r from-primary-600/20 via-primary-500/20 to-secondary-800/20 blur-xl opacity-50"></div>
                    </div>
                    <div className="max-w-2xl">
                      <div className="text-xl font-medium text-gray-700 leading-relaxed">
                        Create, transform, and enhance your images with AI
                      </div>
                      <div className="mt-3 text-sm text-gray-500 font-normal">
                        Professional-grade image editing powered by advanced artificial intelligence
                      </div>
                    </div>
                  </div>
                  <div className="w-full max-w-lg">
                    <label
                      htmlFor="dropzone-file"
                      className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                        isDragging
                          ? "bg-gradient-to-br from-primary-50 to-primary-100/50 border-primary-400 shadow-lg scale-[1.02]"
                          : "bg-gradient-to-br from-gray-50 to-white border-gray-300 hover:border-primary-300 hover:bg-gradient-to-br hover:from-primary-50/30 hover:to-white hover:shadow-md"
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center pt-8 pb-8 px-6">
                          <AiOutlineCloudUpload className="w-12 h-12 text-gray-500 mb-4 transition-colors duration-300 group-hover:text-primary-500" />
                          <p className="mb-3 text-base font-medium text-gray-700">
                            <span className="font-semibold text-gray-800">Click to upload</span>{" "}
                            or drag and drop
                          </p>
                          <p className="text-sm text-gray-500 font-normal">
                            PNG, JPG, GIF up to 10MB
                          </p>
                        </div>
                        <input id="dropzone-file" type="file" className="hidden" onChange={handleFileSelected} />
                      </label>
                    <div className="mt-4 text-center">
                      <span className="text-gray-500">Or</span>
                      <button
                        onClick={() => setShowGalleryModal(true)}
                        className="w-full flex items-center justify-center mx-auto mt-4 px-6 py-3 bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 text-primary-700 rounded-lg transition-all duration-300 border border-primary-200 hover:border-primary-300 hover:shadow-md font-medium"
                      >
                        <PiImage className="w-5 h-5 mr-2 text-primary-600" />
                        Select From Gallery
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full relative"
                  style={{ height: containerHeight }}
                >
                  <div
                    ref={containerRef}
                    className={`w-full h-full overflow-auto ${
                      zoom > 100 && !isCropping ? "cursor-grab" : ""
                    }`}
                    onMouseDown={
                      zoom > 100 && !isCropping ? handleMouseDown : undefined
                    }
                    onMouseMove={
                      zoom > 100 && !isCropping ? handleMouseMove : undefined
                    }
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                      position: "relative",
                      overflow: "auto",
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
                    }}
                  >
                    <div
                      style={{
                        padding:
                          zoom > 100
                            ? `${Math.max(zoom * 1.5, 150)}px`
                            : `${20 + extraPadding}px`,
                        minHeight: "100%",
                        minWidth: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <div
                        className="image-container"
                        style={{
                          transform: `scale(${zoom / 100})`,
                          transformOrigin: "center",
                          transition: "transform 0.2s ease-out",
                          maxHeight: "100%",
                          maxWidth: "100%",
                          position: "relative",
                        }}
                      >
                        {isCompareMode && undoStack.length > 0 ? (
                          <div className="relative w-full h-full flex items-center justify-center">
                            <div className="w-full h-full flex items-center justify-center">
                              <ReactCompareSlider
                                key={`${
                                  undoStack[undoStack.length - 1]
                                }-${currentImage}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  maxWidth: "100%",
                                  maxHeight: `100%`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                position={50}
                                itemOne={
                                  <div className="w-full h-full flex items-center justify-center overflow-hidden">
                                    <ReactCompareSliderImage
                                      src={undoStack[undoStack.length - 1]}
                                      srcSet={undoStack[undoStack.length - 1]}
                                      alt="Previous image"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        objectPosition: "center",
                                      }}
                                    />
                                  </div>
                                }
                                itemTwo={
                                  <div className="w-full h-full flex items-center justify-center overflow-hidden">
                                    <ReactCompareSliderImage
                                      src={currentImage}
                                      srcSet={currentImage}
                                      alt="Current image"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        objectPosition: "center",
                                      }}
                                    />
                                  </div>
                                }
                              />
                            </div>
                          </div>
                        ) : (
                          <div
                            className="relative flex items-center justify-center"
                            ref={cropContainerRef}
                          >
                            <div className="relative">
                              <img
                                ref={imageRef}
                                src={currentImage}
                                alt="Current"
                                className="max-w-full max-h-full object-contain"
                                draggable={false}
                                style={{
                                  display: "block",
                                  maxHeight: "calc(100vh - 100px)",
                                  maxWidth: "calc(100vw - 150px)",
                                  objectFit: "contain",
                                  margin: "auto",
                                }}
                                onLoad={(e) => {
                                  // Force refresh of container dimensions on image load
                                  if (containerRef.current) {
                                    const container = containerRef.current;
                                    const img = e.target;

                                    // Calculate the scale to fit the image within viewport
                                    const scaleX =
                                      (container.clientWidth - 40) /
                                      img.naturalWidth;
                                    const scaleY =
                                      (container.clientHeight - 40) /
                                      img.naturalHeight;
                                    const scale = Math.min(scaleX, scaleY, 1);

                                    // If image is larger than viewport, set initial zoom to fit
                                    if (scale < 1) {
                                      setZoom(Math.floor(scale * 100));
                                    }

                                    // Center the image
                                    setTimeout(() => {
                                      container.scrollLeft =
                                        (container.scrollWidth -
                                          container.clientWidth) /
                                        2;
                                      container.scrollTop =
                                        (container.scrollHeight -
                                          container.clientHeight) /
                                        2;
                                    }, 50);
                                  }
                                }}
                              />
                              {isCropping && (
                                <>
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      position: "absolute",
                                      width: "100%",
                                      height: "100%",
                                      zIndex: 10,
                                    }}
                                  >
                                    <canvas
                                      ref={cropCanvasRef}
                                      className="absolute inset-0 w-full h-full cursor-crosshair"
                                      onMouseDown={handleCropMouseDown}
                                      onMouseMove={handleCropMouseMove}
                                      onMouseUp={handleCropMouseUp}
                                      onMouseLeave={handleCropMouseUp}
                                      style={{
                                        touchAction: "none",
                                        userSelect: "none",
                                        WebkitUserSelect: "none",
                                        MozUserSelect: "none",
                                        msUserSelect: "none",
                                        pointerEvents: "auto",
                                      }}
                                    />
                                  </div>
                                  <div className="absolute top-2 right-2 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-lg z-20">
                                    <button
                                      onClick={applyCrop}
                                      className="w-6 h-6 flex items-center justify-center text-white bg-green-500 hover:bg-green-600 rounded-md shadow-sm transition-colors group"
                                      title="Apply Crop"
                                    >
                                      <IoCheckmarkDoneSharp className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                                    </button>
                                    <button
                                      onClick={cancelCrop}
                                      className="w-6 h-6 flex items-center justify-center text-white bg-red-500 hover:bg-red-600 rounded-md shadow-sm transition-colors group"
                                      title="Cancel Crop"
                                    >
                                      <IoCloseCircle className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Delete button */}
                  {/* <button 
                                        onClick={() => setSelectedImage(null)} 
                                        className="absolute top-4 right-4 p-2 bg-red-500 rounded-full shadow-lg hover:bg-red-600 transition-colors z-10"
                                    >
                                        <img src={DeleteIcon} alt="Remove" className="w-5 h-5" />
                                    </button> */}
                </div>
              )}

              {/* Zoom controls - only show when there is an image */}
              {currentImage && (
                <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white/95 rounded-lg px-3 py-2 shadow-md z-10">
                  <button
                    onClick={() => handleZoomChange(zoom - 10)}
                    className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full"
                    disabled={zoom <= 50}
                  >
                    -
                  </button>
                  <div className="relative w-24 h-7 flex items-center">
                    <input
                      type="range"
                      min="50"
                      max="150"
                      step="10"
                      value={zoom}
                      onChange={(e) => handleZoomChange(Number(e.target.value))}
                      className="w-full h-1.5 bg-gray-200 rounded-full appearance-none"
                      style={{
                        margin: 0,
                      }}
                    />
                  </div>
                  <button
                    onClick={() => handleZoomChange(zoom + 10)}
                    className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full"
                    disabled={zoom >= 150}
                  >
                    +
                  </button>
                  <span className="text-xs font-medium text-gray-600 min-w-[2.5rem] text-center">
                    {zoom}%
                  </span>
                </div>
              )}

              <InputContainer
                isProcessing={isProcessing}
                handleGenerateEditImage={handleGenerateEditImage}
                generationSettings={generationSettings}
                onSettingsChange={setGenerationSettings}
                selectedPrompt={selectedPrompt}
                setShowPromptGalleryModal={setShowPromptGalleryModal}
              />
            </div>
            <div
              className="absolute top-0 right-0 p-1 h-full"
              style={{ height: containerHeight }}
            >
              <div className="h-full flex justify-center gap-4">
                {currentImage && showChat && (
                  <ChatInterface
                    showChat={showChat}
                    setShowChat={setShowChat}
                    chatInput={chatInput}
                    setChatInput={setChatInput}
                    chatResponses={chatResponses}
                    isChatProcessing={isChatProcessing}
                    handleChatSubmit={handleChatSubmit}
                  />
                )}
                {/* Side Background Drawer */}
                {addBackgroundMode && (
                  
                    
                    <ChangeBackground
                      addBackground={addBackground}
                      setCurrentImage={setCurrentImage}
                      imageWithTransparentBackground={
                        imageWithTransparentBackground
                      }
                      setAddBackgroundMode={setAddBackgroundMode}
                      handleTool={handleTool}
                    />
                
                )}
                <div className="flex flex-col rounded-lg mr-4">
                  <div className="px-3 py-2 text-sm font-bold rounded-t-lg text-white bg-secondary text-center">
                    Tools
                  </div>
                  <div className="flex flex-col divide-y divide-gray-200/80 rounded-lg shadow-lg border border-gray-200/80">
                  <button
                    onClick={() =>
                      handleTool("remove_background", null, null, true)
                    }
                    disabled={isProcessing || currentImage === null}
                    className="px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative"
                    title="Remove Background"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <PiMagicWand className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Remove BG
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>

                  {/* Add Background Button */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        setAddBackgroundMode(!addBackgroundMode);
                        setResizeImage(false);
                        setIsCropping(false);
                        setIsCompareMode(false);
                        setIsCaptioning(false);
                        setShowCaptionOptions(false);
                        setShowChat(false);
                        setChatInput("");
                        setChatResponses([]);
                      }}
                      disabled={!currentImage || isProcessing}
                      className={`w-full px-2 py-3 text-sm font-medium text-gray-600 hover:text-red-600 bg-white hover:bg-red-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                        addBackgroundMode
                          ? "text-white bg-gradient-to-b from-rose-500 to-rose-600 hover:bg-gradient-to-b hover:from-rose-600 hover:to-rose-700"
                          : ""
                      }`}
                      title="Add Background"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <TbBackground
                          className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                            addBackgroundMode ? "text-white" : ""
                          }`}
                        />
                      </div>
                      {!addBackgroundMode && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                          Change BG
                          <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                        </div>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={() =>
                      handleTool("upscale_image", null, null, false)
                    }
                    disabled={isProcessing || currentImage === null}
                    className="px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative"
                    title="Upscale Image"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <PiResize className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Upscale
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>

                  {/* resize */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        setResizeImage(!resizeImage);
                        setAddBackgroundMode(false);
                        setIsCropping(false);
                        setIsCompareMode(false);
                        setIsCaptioning(false);
                        setShowCaptionOptions(false);
                        setShowChat(false);
                        setChatInput("");
                        setChatResponses([]);
                      }}
                      disabled={!currentImage || isProcessing}
                      className={`w-full px-2 py-3 text-sm font-medium text-gray-600 hover:text-red-600 bg-white hover:bg-red-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                        resizeImage
                          ? "text-white bg-gradient-to-b from-rose-500 to-rose-600 hover:bg-gradient-to-b hover:from-rose-600 hover:to-rose-700"
                          : ""
                      }`}
                      title="Resize"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <IoResize
                          className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                            resizeImage ? "text-white" : ""
                          }`}
                        />
                      </div>

                      {!resizeImage && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                          Resize
                          <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                        </div>
                      )}
                    </button>

                    {resizeImage && (
                      <div
                        className="absolute right-full top-1/2 -translate-y-1/4 mr-2 bg-white rounded-lg shadow-xl border border-gray-200/80"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Resize
                          setResizeImage={setResizeImage}
                          currentImage={currentImage}
                          setCurrentImage={setCurrentImage}
                          saveImageToHistory={saveImageToHistory}
                          setImageWithTransparentBackground={setImageWithTransparentBackground}
                        />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleCrop}
                    className={`px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                      isCropping
                        ? "text-white bg-gradient-to-b from-rose-500 to-rose-600 hover:bg-gradient-to-b hover:from-rose-600 hover:to-rose-700"
                        : ""
                    }`}
                    title="Crop Image"
                    disabled={isProcessing || currentImage === null}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <PiCrop
                        className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                          isCropping ? "text-white" : ""
                        }`}
                      />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Crop
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>
                  <div className="relative">
                    <button
                      onClick={handleCaption}
                      className={`w-full px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                        isCaptioning
                          ? "text-white bg-gradient-to-b from-rose-500 to-rose-600 hover:bg-gradient-to-b hover:from-rose-600 hover:to-rose-700 hover:text-white"
                          : ""
                      }`}
                      title="Caption Image"
                      disabled={isProcessing || currentImage === null}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <PiClosedCaptioning className="h-6 w-6 group-hover:scale-110 transition-transform" />
                      </div>
                      {!showCaptionOptions && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                          Caption
                          <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                        </div>
                      )}
                      {showCaptionOptions && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
                          <button
                            onClick={() => handleCaptionOption("json")}
                            className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors border-b border-gray-200"
                          >
                            <div className="flex flex-col items-center justify-center">
                              <MdOutlineCode className="h-4 w-4  group-hover:scale-110 transition-transform" />
                              <span className="text-xs mt-1">JSON</span>
                            </div>
                          </button>
                          <button
                            onClick={() => handleCaptionOption("text")}
                            className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                          >
                            <div className="flex flex-col items-center justify-center">
                              <MdOutlineTextFields className="h-4 w-4 group-hover:scale-110 transition-transform" />
                              <span className="text-xs mt-1">Text</span>
                            </div>
                          </button>
                        </div>
                      )}
                    </button>
                    {showCaptionRequestResponse && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                        {captionRequestResponse}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleChatToggle}
                    className={`px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                      showChat
                        ? "text-white bg-gradient-to-b from-rose-500 to-rose-600 hover:bg-gradient-to-b hover:from-rose-600 hover:to-rose-700"
                        : ""
                    }`}
                    title="Chat"
                    disabled={isProcessing || currentImage === null}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <IoChatbubblesOutline
                        className={`h-6 w-6 group-hover:scale-110 transition-transform ${
                          showChat ? "text-white" : ""
                        }`}
                      />
                    </div>
                    {!showChat && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                        Chat
                        <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                      </div>
                    )}
                  </button>

                  <button
                    onClick={handleUndo}
                    className="px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative"
                    title="Undo Image"
                    disabled={undoStack.length === 0}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <IoArrowUndoOutline className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Undo
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>
                  <button
                    onClick={handleRedo}
                    className="px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative"
                    title="Redo Image"
                    disabled={redoStack.length === 0}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <IoArrowRedoOutline className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Redo
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>
                  <button
                    onClick={handleCompareToggle}
                    disabled={
                      isProcessing ||
                      currentImage === null ||
                      undoStack.length === 0
                    }
                    className={`px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative ${
                      isCompareMode
                        ? "text-white bg-gradient-to-b from-rose-500 to-rose-600 hover:bg-gradient-to-b hover:from-rose-600 hover:to-rose-700 rounded-b-none border-b-2 border-rose-300"
                        : ""
                    }`}
                    title="Compare Images"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <MdOutlineCompare
                        className={`h-6 w-6 group-hover:scale-110 transition-transform`}  
                      />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Compare
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleSaveToGallery()}
                    disabled={isProcessing || currentImage === null}
                    className="px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative"
                    title="Save To Gallery"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <LuImageDown className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Save To Gallery
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>
                  <button
                    onClick={() => handleDownloadImage()}
                    disabled={isProcessing || currentImage === null}
                    className="px-2 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative"
                    title="Download Image"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <MdOutlineFileDownload className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Download
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      if (currentImage) {
                        // Exit crop mode if active
                        if (isCropping) {
                          setIsCropping(false);
                        }

                        // Disable chat mode
                        if (showChat) {
                          setShowChat(false);
                        }

                        // Save current image to history before removing
                        saveImageToHistory(null);
                        setCurrentImage(null);

                        // Disable compare mode when removing an image
                        if (isCompareMode) {
                          setIsCompareMode(false);
                        }

                        if (isCaptioning) {
                          setIsCaptioning(false);
                          setShowCaptionOptions(false);
                        }

                        // Clear the undo stack
                        setUndoStack([]);
                        setRedoStack([]);
                      }

                      setGenerationSettings(defaultGenerationSettings);
                    }}
                    className="px-2 py-3 text-sm font-medium text-gray-600 rounded-b-lg hover:text-rose-600 bg-white hover:bg-rose-50/50 transition-all duration-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 group relative"
                    title="Clear All"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <MdOutlineDeleteSweep className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900/90 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                      Clear All
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
                    </div>
                  </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="size-20 grow" style={{ height: containerHeight }}>
            <div
              className="flex items-center justify-center max-w-2xl mx-auto"
              style={{ height: containerHeight }}
            >
              <AuthenticationRequired
                handleLoginRedirect={() => navigate("/signin")}
                message="Please login to use the studio"
              />
            </div>
          </div>
        )}

        {isProcessing && <ProcessingStatus />}
      </div>

      {showGalleryModal && (
        <GalleryModal
          setImage={handleGalleryImageSelect}
          setSelectedObj={setSelectedObj}
          setShowGalleryModal={setShowGalleryModal}
        />
      )}

      {showToastErrorMessage && (
        <div className="absolute top-4 left-0 right-0 flex justify-center z-30">
          <div
            id="toast-default"
            className="flex items-center w-full max-w-md p-4 text-gray-500 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border-l-4 border-red-500 animate-fade-in-down"
            role="alert"
          >
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-red-500 bg-red-100 rounded-lg">
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293a1 1 0 0 1-1.414 1.414L10 11.414z" />
              </svg>
              <span className="sr-only">Error icon</span>
            </div>
            <div className="flex flex-col mr-4">
              <div className="ms-3 text-base font-semibold text-red-500">
                {errorStatus}
              </div>
              <div className="ms-3 text-sm font-normal text-red-400">
                {errorMessage}
              </div>
            </div>
            <button
              type="button"
              className="ms-auto -mx-1.5 -my-1.5 bg-white/80 text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8"
              data-dismiss-target="#toast-default"
              aria-label="Close"
              onClick={handleErrorClose}
            >
              <span className="sr-only">Close</span>
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {showToastSuccessMessage && (
        <div className="absolute top-4 left-0 right-0 flex justify-center z-30">
          <div
            id="toast-success"
            className="flex items-center w-full max-w-md p-4 text-gray-500 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border-l-4 border-green-500 animate-fade-in-down"
            role="alert"
          >
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded-lg">
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
              </svg>
              <span className="sr-only">Success icon</span>
            </div>
            <div className="ms-3 text-sm font-semibold text-green-600">
              {successMessage}
            </div>
            <button
              type="button"
              className="ms-auto -mx-1.5 -my-1.5 bg-white/80 text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8"
              data-dismiss-target="#toast-success"
              aria-label="Close"
              onClick={handleSuccessClose}
            >
              <span className="sr-only">Close</span>
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      {showCaptionRequestResponse && <CaptionResponse />}
    </div>
  );
};

export default Studio;
